import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * iter72l · Lightweight modal video player used by the Clubs and Vendors
 * marketing pages. Accepts a direct video URL (MP4/WebM) OR a YouTube /
 * Vimeo embed URL. Autoplays with sound muted (browser policy), pauses +
 * unmounts on close.
 */
export default function VideoModal({ open, src, title, poster, onClose }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Escape closes the modal.
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while the modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      try { videoRef.current?.pause(); } catch { /* noop */ }
    };
  }, [open, onClose]);

  if (!open) return null;

  const isEmbed = /youtube\.com|youtu\.be|vimeo\.com/i.test(src || "");

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-modal"
    >
      <div
        className="relative w-full max-w-5xl aspect-video bg-black border border-[#B59A5A]/40 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="video-modal-close"
          aria-label="Close video"
          className="absolute -top-3 -right-3 z-10 w-9 h-9 bg-white text-[#1A422D] flex items-center justify-center hover:bg-[#F3F1E9] border border-[#B59A5A] shadow"
        >
          <X size={16} />
        </button>
        {!src ? (
          <div className="absolute inset-0 flex items-center justify-center text-[#F9F8F4] px-6 text-center">
            <div>
              <div className="overline text-[#B59A5A]">Video coming soon</div>
              <p className="mt-2 text-sm opacity-80">This intro video hasn&rsquo;t been uploaded yet.</p>
            </div>
          </div>
        ) : isEmbed ? (
          <iframe
            key={src}
            src={_toEmbed(src)}
            title={title || "Watch video"}
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            key={src}
            ref={videoRef}
            src={src}
            poster={poster || undefined}
            className="w-full h-full"
            controls
            autoPlay
            playsInline
            data-testid="video-modal-player"
          />
        )}
      </div>
    </div>
  );
}

function _toEmbed(url) {
  try {
    if (/youtu\.be\//.test(url)) {
      const id = url.split("youtu.be/")[1].split(/[?&]/)[0];
      return `https://www.youtube.com/embed/${id}?autoplay=1`;
    }
    if (/youtube\.com\/watch/.test(url)) {
      const u = new URL(url);
      const id = u.searchParams.get("v");
      return `https://www.youtube.com/embed/${id}?autoplay=1`;
    }
    if (/vimeo\.com\/\d+/.test(url)) {
      const id = url.match(/vimeo\.com\/(\d+)/)[1];
      return `https://player.vimeo.com/video/${id}?autoplay=1`;
    }
  } catch { /* fall through */ }
  return url;
}
